/*
 * Project:      mkad-frontend
 * Developed by: Juan David Pelaez Cumbe
 * Date:         20/12/23 - 3:34 PM
 * Module name:  url-service.constants.ts
 * File name:    url-service.constants.ts
 * IDE:          WebStorm
 */

interface BasicEndpointUrlInterface {
  base: string;
  data: string;
}

interface UpdateEndpointUrlInterface {
  base: string;
  update: string;
  get?: string;
}

interface CrudEndpointUrlInterface {
  base: string;
  list: string;
  get: string;
  create: string;
  update: string;
  summary: string;
  delete: string;
}

interface UserEndpointUrlInterface extends CrudEndpointUrlInterface {
  createSuperAdmin: string;
}

interface DeviceModelEndpointsInterface extends CrudEndpointUrlInterface {
  getVariablesByModel: string;
  validateFormula: string;
}

interface ParametersEndpointUrlInterface extends CrudEndpointUrlInterface {
  summaryParent: string;
}

interface CustomerEndpointUrlInterface extends CrudEndpointUrlInterface {
  summaryContact: string;
  parentSummary: string;
  parentUpdate: string;
  levelsList: string;
  stats: string;
}

interface DeviceEndpointUrlInterface extends CrudEndpointUrlInterface {
  varLimit: string;
  register: string;
}

interface LicensesEndpointUrlInterface {
  base: string;
  summary: string;
  customerUser: string;
  customerDevice: string;
  licenseByContract: string;
}

interface CoreEndpointUrlInterface {
  base: string;
  about: BasicEndpointUrlInterface;
  status: BasicEndpointUrlInterface;
  lang: string;
  timezone: string;
  dialCodes: string;
  validateFormula: string;
}

interface MeerkadEndpointInterface {
  customer: CustomerEndpointUrlInterface;
  contract: CrudEndpointUrlInterface;
  licenses: LicensesEndpointUrlInterface;
  contacts: CrudEndpointUrlInterface;
  group: CrudEndpointUrlInterface;
  user: UserEndpointUrlInterface;
  location: CrudEndpointUrlInterface;
  device: DeviceEndpointUrlInterface;
}

interface ManageEndpointUrlInterface {
  base: string;
  start: string;
  end: string;
}

interface ProfileEndpointUrlInterface extends UpdateEndpointUrlInterface {
  setFilter: string;
  deleteFilter: string;
}

interface AuthEndpointUrlInterface {
  base: string;
  login: string;
  logout: string;
  refresh: string;
  requestPassword: string;
  changePassword: string;
  profile: ProfileEndpointUrlInterface;
  settings: UpdateEndpointUrlInterface;
  permission: UpdateEndpointUrlInterface;
  manage: ManageEndpointUrlInterface;
}

interface FilterEndpointUrlInterface {
  base: string;
  update: string;
  delete: string;
}
interface HomeEndpointUrlInterface {
  base: string;
  list: string;
  filter: FilterEndpointUrlInterface;
  deviceDetail: string;
}

interface EndpointUrlInterface {
  core: CoreEndpointUrlInterface;
  meerkad: MeerkadEndpointInterface;
  system: {
    sensorUtils: BasicEndpointUrlInterface;
    sensors: CrudEndpointUrlInterface;
    parameters: ParametersEndpointUrlInterface;
    commands: CrudEndpointUrlInterface;
    icons: CrudEndpointUrlInterface;
    deviceModel: DeviceModelEndpointsInterface;
    deviceSensorUtils: BasicEndpointUrlInterface;
    devices: CrudEndpointUrlInterface;
  };
  devices: {
    triggers: CrudEndpointUrlInterface;
    enumTrigger: BasicEndpointUrlInterface;
    deviceCustomization: CrudEndpointUrlInterface;
    deviceModelPort: BasicEndpointUrlInterface;
    customerDeviceSensor: BasicEndpointUrlInterface;
    enumCustomerDeviceVarLimit: BasicEndpointUrlInterface;
    variable: BasicEndpointUrlInterface;
  };
  auth: AuthEndpointUrlInterface;
  home: HomeEndpointUrlInterface;
}

/**
 * @naming
 * base: for prefix of the feature or module
 * get: for query a specific item
 * list: for query the list
 * summary: for query the list specify
 * update: for update a specific item
 * create: for create an item
 * delete: for delete a specific item
 * toggle: for delete a specific item
 */
export const URLS_SERVICE: EndpointUrlInterface = {
  core: {
    base: 'core/',
    about: {
      base: 'about/',
      data: 'data/',
    },
    status: {
      base: 'client/',
      data: 'status/',
    },
    lang: 'language/',
    timezone: 'timezone/',
    dialCodes: 'dialcodes/',
    validateFormula: 'validate_formula/',
  },
  meerkad: {
    customer: {
      base: 'customer/',
      list: 'list/',
      delete: 'delete/',
      create: 'create/',
      update: 'update/',
      summary: 'summary/',
      summaryContact: 'summary-contact/',
      get: 'get/',
      parentSummary: 'parent/summary/',
      parentUpdate: 'parent/update/',
      levelsList: 'enum/customer/',
      stats: 'stats/',
    },
    contract: {
      base: 'contract/',
      list: 'list/',
      summary: 'summary/',
      delete: 'delete/',
      create: 'create/',
      get: 'get/',
      update: 'update/',
    },
    licenses: {
      base: 'license/',
      summary: 'summary/',
      customerUser: 'user',
      customerDevice: 'device',
      licenseByContract: 'contact'
    },
    contacts: {
      base: 'contact/',
      list: 'list/',
      delete: 'delete/',
      create: 'create/',
      update: 'update/',
      get: 'get/',
      summary: '',
    },
    group: {
      base: 'group/',
      summary: 'summary/',
      get: 'get/',
      list: 'list/',
      delete: 'delete/',
      create: 'create/',
      update: 'update/',
    },
    user: {
      base: 'user/',
      summary: 'summary/',
      get: 'get/',
      list: 'list/',
      delete: 'delete/',
      create: 'create/',
      createSuperAdmin: 'superadmin/create/',
      update: 'update/',
    },
    location: {
      base: 'location/',
      summary: 'summary/',
      get: 'get/',
      list: 'list/',
      delete: 'delete/',
      create: 'create/',
      update: 'update/',
    },
    device: {
      base: 'device/',
      summary: 'summary/',
      get: 'get/',
      list: 'list/',
      delete: 'delete/',
      create: 'create/',
      update: 'update/',
      varLimit: 'summary_var_limit/',
      register: 'register'
    },
  },
  system: {
    sensorUtils: {
      base: 'enum/',
      data: 'sensor/',
    },
    sensors: {
      base: 'sensor/',
      get: 'get/',
      list: 'list/',
      create: 'create/',
      update: 'update/',
      delete: 'delete/',
      summary: 'summary/',
    },
    parameters: {
      base: 'parameter/',
      get: 'get/',
      list: 'list/',
      create: 'create/',
      update: 'update/',
      delete: 'delete/',
      summary: 'summary/',
      summaryParent: 'summary-parent/',
    },
    commands: {
      base: 'command/',
      get: 'get/',
      list: 'list/',
      create: 'create/',
      update: 'update/',
      delete: 'delete/',
      summary: 'summary/',
    },
    icons: {
      base: 'icon/',
      get: 'get/',
      list: 'list/',
      create: 'create/',
      update: 'update/',
      delete: 'delete/',
      summary: 'summary/',
    },
    deviceModel: {
      base: 'device_model/',
      get: 'get/',
      list: 'list/',
      create: 'create/',
      update: 'update/',
      delete: 'delete/',
      summary: 'summary/',
      getVariablesByModel: 'portvars/',
      validateFormula: 'validate_formula'
    },
    deviceSensorUtils: {
      base: 'enum/',
      data: 'device_model/',
    },
    devices: {
      base: 'device/',
      get: 'get/',
      list: 'list/',
      create: 'create/',
      update: 'update/',
      delete: 'delete/',
      summary: 'summary/',
    },
  },
  devices: {
    deviceCustomization: {
      base: 'device/',
      get: 'get/',
      list: 'list/',
      create: 'create/',
      update: 'update/',
      delete: 'delete/',
      summary: 'summary/',
    },
    triggers: {
      base: 'trigger/',
      summary: 'summary/',
      get: 'get/',
      list: 'list/',
      delete: 'delete/',
      create: 'create/',
      update: 'update/',
    },
    enumTrigger: {
      base: 'enum/',
      data: 'trigger/',
    },
    deviceModelPort:{
      base: 'device_model_port/',
      data: 'summary/',
    },
    customerDeviceSensor:{
      base: 'sensor/',
      data: 'custom_vars/',
    },
    enumCustomerDeviceVarLimit:{
      base: 'enum/',
      data: 'customer_device/',
    },
    variable :{
      base: 'var/',
      data: 'get/',
    },
  },
  auth: {
    base: 'auth/',
    login: 'xDQ4d7p7kkCK8VAA3/',
    logout: 'logout/',
    refresh: 'j6UvfcGuSCbKDK65R/',
    requestPassword: 'reset-password/',
    changePassword: 'change-password/',
    profile: {
      base: 'user/',
      get: 'profile/',
      update: 'update/',
      setFilter: 'set-filter/',
      deleteFilter: 'delete-filter/',
    },
    settings: {
      base: 'app-settings/',
      update: 'update/',
    },
    permission: {
      base: 'permission/',
      update: 'update/',
      get: 'list/',
    },
    manage: {
      base: 'manage/',
      start: 'start/',
      end: 'close/',
    },
  },
  home: {
    base: 'home/',
    list: 'client/list/',
    filter: {
      base: 'filter/',
      update: 'update/',
      delete: 'delete/',
    },
    deviceDetail: 'detail/',
  },
};
